/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import { getOrientation } from 'get-orientation/browser';
import humanize from 'humanize-plus';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import { ButtonBase } from '@mui/material';

import { MAX_PHOTO_FILE_SIZE_IN_BYTES, ORIENTATION_TO_ANGLE } from '../../constants/settings';
import { Namespaces } from '../../i18n';
import { ConvertibleCanvas } from '../../miscellaneous/ConvertibleCanvas';
import { ThumbnailOption } from '../../types/thumbnail/ThumbnailOption';
import { getRotatedImage, readFile } from '../../utils/canvasUtils';
import { useObjectState } from '../../utils/useObjectState';
import { EDIT_PHOTO_ACCEPTABLE_FILES } from '../EditPhoto/EditPhoto';
import { EditPhotoModal } from '../EditPhotoModal/EditPhotoModal';
import { useEventCallback } from '../hooks/useEventCallback';
import { ThumbnailSelectorItem } from './ThumbnailSelector.Item';
import { useThumbnailSelectorStyles } from './ThumbnailSelector.styles';
import { ThumbnailSelectorType } from './ThumbnailSelector.types';

type ImageUploadProps =
  | { allowUploadImage: true; onImageUpload: (thumbnail: Blob) => any }
  | { allowUploadImage?: false; onImageUpload?: undefined };

type ThumbnailSelectorProps = ImageUploadProps & {
  onItemClick: (value: ThumbnailOption) => any;
  options: ThumbnailOption[];
  value?: null | ThumbnailOption;
  type: ThumbnailSelectorType;
};

export { ThumbnailSelectorType };

type State = {
  openModal: boolean;
  editImageSrc: string;
};

export function ThumbnailSelector(props: ThumbnailSelectorProps) {
  const { allowUploadImage, onImageUpload, options, onItemClick, value, type } = props;
  const classes = useThumbnailSelectorStyles();
  const { t } = useTranslation([Namespaces.Common]);

  const [state, setState] = useObjectState<State>({
    editImageSrc: '',
    openModal: false,
  });

  const { openModal, editImageSrc } = state;

  const onFileInputChange = useEventCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      e.target.files = null;
      e.target.value = '';

      if (file.size > MAX_PHOTO_FILE_SIZE_IN_BYTES) {
        alert(
          t('warnings.exceeded_file_size_with_max', {
            maxSize: humanize.fileSize(MAX_PHOTO_FILE_SIZE_IN_BYTES),
          }),
        );
        return;
      }

      let imageDataUrl = await readFile(file);
      const orientation = await getOrientation(file);
      const rotation = ORIENTATION_TO_ANGLE[orientation as number] ?? ORIENTATION_TO_ANGLE.__fallback;

      if (rotation) {
        imageDataUrl = await (await getRotatedImage(imageDataUrl, rotation)).toBase64();
      }

      setState({ editImageSrc: imageDataUrl, openModal: true });
    }
  });

  const onCancel = () => {
    setState({ editImageSrc: '', openModal: false });
  };

  const onSave = async (canvas: ConvertibleCanvas) => {
    const blob = await canvas.toBlob();
    onImageUpload!(blob);
    onCancel();
  };

  return (
    <div className={classes.root}>
      <div className={classes.list}>
        {options.map((opt, index) => (
          <div className={classes.listItem} key={[index].join('_')}>
            <ThumbnailSelectorItem
              option={opt}
              onClick={onItemClick}
              isActive={value === opt}
              type={type}
              data-cy={'thumbnail-upload-button'}
            />
          </div>
        ))}
        {allowUploadImage && (
          <div className={classes.listItem}>
            <ButtonBase className={classes.addButton}>
              <AddIcon />
              <input type="file" onChange={onFileInputChange} accept={EDIT_PHOTO_ACCEPTABLE_FILES} />
            </ButtonBase>
          </div>
        )}
      </div>
      <EditPhotoModal open={openModal} imageSrc={editImageSrc} onSave={onSave} onCancel={onCancel} size="big" />
    </div>
  );
}
